// Leads from the marketing site (tallerza.cl): demo requests and the waiting
// list, stored in `contactos/{id}` by the `contacto` Function. Pure helpers
// for the superadmin's Contactos page: filters (in the URL), labels and the
// WhatsApp / mail links.
import {
  ESTADOS_CONTACTO,
  ESTADOS_CONTACTO_ABIERTOS,
  ETIQUETAS_TAMANO,
  ETIQUETAS_TIPO_TALLER,
  type ContactoDemo,
  type ContactoEspera,
  type ConversionContacto,
  type CuponDelContacto,
  type EnvioCorreoTipo,
  type DetalleCuponNoAplica,
  type EstadoContacto,
  type EstadoContactoManual,
  type InvitacionContacto,
  type RetencionContacto,
} from '@taller/shared';
import { FirebaseError } from 'firebase/app';
import { z } from 'zod';
import { aMillis, type ConId, type MarcaTiempo } from '@/datos/tipos';
import { duracionDe, textoBeneficio } from './cupones';

// Follow-up states (@taller/shared): `cliente` is final and only the
// `convertirContacto` callable sets it (it applies the coupon in the same
// transaction); the superadmin moves a lead by hand only among the manual
// ones, the values firestore.rules accept on update.
export {
  ESTADOS_CONTACTO,
  ESTADOS_CONTACTO_MANUALES as ESTADOS_EDITABLES,
  MAX_MOTIVO_PERDIDA,
  ETIQUETAS_ESTADO_CONTACTO as ETIQUETAS_ESTADO,
  type EstadoContacto,
  type EstadoContactoManual as EstadoEditable,
} from '@taller/shared';

/**
 * Server fields; `convertido*` only once `convertirContacto` ran, the
 * invitation fields (`tallerId`, `invitado*`, `pruebaHasta`) once
 * `invitarAPrueba` created its workshop;
 * `motivoPerdida` only while the lead is 'perdido' (optional), `expiraEn`
 * (retention TTL) until it becomes a customer.
 */
type Servidor = {
  estado: EstadoContacto;
  creadoEn?: MarcaTiempo | null;
  motivoPerdida?: string;
  /** Correos tipo sent from Plataforma → Correos (last 20, newest last). */
  correosEnviados?: EnvioCorreoTipo[];
} & Partial<ConversionContacto<MarcaTiempo>> &
  Partial<InvitacionContacto<MarcaTiempo>> &
  RetencionContacto<MarcaTiempo>;

/**
 * What the `contacto` Function stores on top of the form: the snapshot of a
 * valid coupon (the form sends only its code), or the rejected code of a
 * no-JS submit (`cuponInvalido`, the lead is kept anyway). `pagina` and the
 * UTM tags come as the form sent them (@taller/shared).
 */
type Atribucion = { cupon?: CuponDelContacto<MarcaTiempo>; cuponInvalido?: string };

export type ContactoDemoDoc = ConId<Omit<ContactoDemo, 'cupon'> & Atribucion & Servidor>;
export type ContactoEsperaDoc = ConId<Omit<ContactoEspera, 'cupon'> & Atribucion & Servidor>;
export type ContactoDoc = ContactoDemoDoc | ContactoEsperaDoc;

export type ContactoEditable = ContactoDoc & { estado: EstadoContactoManual };

export const esCliente = (c: Pick<ContactoDoc, 'estado'>) => c.estado === 'cliente';
/** No longer an opportunity (its row reads muted). */
export const esCerrado = (c: Pick<ContactoDoc, 'estado'>) =>
  c.estado === 'descartado' || c.estado === 'perdido';

/** Not a client yet: its status can still be changed by hand. */
export const esEditable = (c: ContactoDoc): c is ContactoEditable => !esCliente(c);

export const ETIQUETAS_TIPO_CONTACTO: Record<ContactoDoc['tipo'], string> = {
  demo: 'Demo',
  espera: 'Lista de espera',
};

// ---- Filters (typed search params) ----

export const FILTROS_ESTADO = ['activos', 'todos', ...ESTADOS_CONTACTO] as const;
export type FiltroEstado = (typeof FILTROS_ESTADO)[number];

export const busquedaContactos = z.object({
  tipo: z.enum(['demo', 'espera']).optional().catch(undefined),
  estado: z.enum(FILTROS_ESTADO).optional().catch(undefined),
});
export type FiltrosContactos = z.infer<typeof busquedaContactos>;

/** Default: the open opportunities (new, contacted, demo done, in pilot). */
export const ESTADO_POR_DEFECTO: FiltroEstado = 'activos';

export function cumpleEstado(c: ContactoDoc, f: FiltroEstado | undefined): boolean {
  const filtro = f ?? ESTADO_POR_DEFECTO;
  if (filtro === 'todos') return true;
  if (filtro === 'activos') {
    return (ESTADOS_CONTACTO_ABIERTOS as readonly string[]).includes(c.estado);
  }
  return c.estado === filtro;
}

/** Newest first. A contact without a date yet (pending write) goes on top. */
export function ordenarContactos(lista: readonly ContactoDoc[]): ContactoDoc[] {
  const t = (c: ContactoDoc) => aMillis(c.creadoEn) ?? Number.POSITIVE_INFINITY;
  return [...lista].sort((a, b) => t(b) - t(a));
}

export function filtrarContactos(
  lista: readonly ContactoDoc[],
  f: FiltrosContactos,
): ContactoDoc[] {
  return lista.filter((c) => (!f.tipo || c.tipo === f.tipo) && cumpleEstado(c, f.estado));
}

// ---- Labels (tolerant: the doc may hold a value added to the site later) ----

export const etiquetaTipoTaller = (v: string | undefined) =>
  v ? (ETIQUETAS_TIPO_TALLER[v as keyof typeof ETIQUETAS_TIPO_TALLER] ?? v) : undefined;

export const etiquetaTamano = (v: string | undefined) =>
  v ? (ETIQUETAS_TAMANO[v as keyof typeof ETIQUETAS_TAMANO] ?? v) : undefined;

// ---- Attribution ----

/**
 * "google / cpc / lanzamiento". A missing middle part shows as "—" so each
 * position keeps its meaning; missing trailing parts are dropped.
 */
export function resumenUtm(c: ContactoDoc): string | undefined {
  const partes = [c.utm_source, c.utm_medium, c.utm_campaign].map((v) => v?.trim() || undefined);
  while (partes.length > 0 && partes[partes.length - 1] === undefined) partes.pop();
  return partes.length ? partes.map((v) => v ?? '—').join(' / ') : undefined;
}

// ---- Links ----

/**
 * wa.me link with a prefilled greeting. `numero` is stored normalized
 * ("+569XXXXXXXX"); wa.me wants the digits only.
 */
export function enlaceWhatsapp(numero: string, nombre?: string): string {
  const digitos = numero.replace(/\D/g, '');
  const saludo = nombre?.trim() ? `Hola ${nombre.trim()}` : 'Hola';
  const texto = `${saludo}, te escribimos de Tallerza por la demo que pediste.`;
  return `https://wa.me/${digitos}?text=${encodeURIComponent(texto)}`;
}

/** "+56912345678" → "+56 9 1234 5678" (as people write it in Chile). */
export function formatearWhatsapp(numero: string): string {
  const m = /^\+56(9)(\d{4})(\d{4})$/.exec(numero);
  return m ? `+56 ${m[1]} ${m[2]} ${m[3]}` : numero;
}

export const enlaceCorreo = (correo: string) => `mailto:${encodeURIComponent(correo)}`;

/** Who the row is about, for labels ("Estado de …"). */
export function nombreContacto(c: ContactoDoc): string {
  if (c.tipo === 'demo') return c.taller ? `${c.nombre} (${c.taller})` : c.nombre;
  return c.correo;
}

// ---- Conversion to client (`convertirContacto`) ----

/** What the confirmation says will happen with the lead's coupon. */
export function textoCuponAlConvertir(c: ContactoDoc): string {
  if (c.cupon) {
    const beneficio = textoBeneficio(c.cupon.tipo, c.cupon.valor, duracionDe(c.cupon));
    return `Se aplicará ${c.cupon.codigo}: ${beneficio}.`;
  }
  if (c.cuponInvalido) {
    return `Llegó con un código que no era válido (${c.cuponInvalido}): no se aplica ningún cupón.`;
  }
  return 'Llegó sin cupón: no se aplica ningún descuento.';
}

/**
 * The callable refuses with `failed-precondition` when the coupon can no
 * longer be applied (exhausted or expired): the dialog then offers to
 * convert without it. Any other error is just shown.
 */
export function esCuponNoAplicable(e: unknown): boolean {
  if (!(e instanceof FirebaseError) || e.code !== 'functions/failed-precondition') return false;
  // The callable sends DetalleCuponNoAplica; without details, trust the code.
  const detalle = (e as FirebaseError & { details?: Partial<DetalleCuponNoAplica> }).details;
  return !detalle || detalle.motivo === 'cupon_no_aplica';
}

/** Coupon outcome of a converted lead, for its row. */
export function textoCuponDeCliente(c: ContactoDoc): string | undefined {
  if (!esCliente(c) || !c.cupon) return undefined;
  if (c.cupon.noAplicado) return 'Cupón no aplicado';
  return 'Cupón aplicado';
}

/** A campaign's funnel: leads with the code and how far they went. */
export type EmbudoCupon = { traen: number; demos: number; pilotos: number; clientes: number };

/**
 * Leads per coupon code, from the contacts already in memory (the shell's
 * listener): how many came with it, how many had a demo, a pilot or became
 * clients (§7.4: CAC per campaign = spend ÷ clients). A later stage counts
 * the earlier ones (a client had its demo); a lost lead counts only as
 * having come, since its status no longer says how far it got.
 */
export function contactosPorCupon(lista: readonly ContactoDoc[]): Map<string, EmbudoCupon> {
  const r = new Map<string, EmbudoCupon>();
  for (const c of lista) {
    if (!c.cupon) continue;
    const x = r.get(c.cupon.codigo) ?? { traen: 0, demos: 0, pilotos: 0, clientes: 0 };
    x.traen += 1;
    if (c.estado === 'demo' || c.estado === 'piloto' || esCliente(c)) x.demos += 1;
    if (c.estado === 'piloto' || esCliente(c)) x.pilotos += 1;
    if (esCliente(c)) x.clientes += 1;
    r.set(c.cupon.codigo, x);
  }
  return r;
}
